'use client';

import { 
  createContext, 
  useContext, 
  useEffect, 
  useState, 
  useCallback,
  useMemo,
  useRef,
  type ReactNode 
} from 'react';
import { AuthProvider as OidcAuthProvider, useAuth as useOidcAuth } from 'react-oidc-context';
import { buildOidcConfig } from '@/lib/auth/config';
import { setTokenProvider, setApiConfig } from '@/lib/api';
import type { RuntimeAuthConfig } from '@/lib/auth/types';

// Role types matching Keycloak configuration
export type UserRole = 'km-viewer' | 'km-editor' | 'km-admin';

export interface User {
  sub: string;
  email: string;
  name: string;
  preferred_username: string;
  roles: UserRole[];
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  login: () => void;
  logout: () => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Extract roles from Keycloak JWT token
 * Roles are stored in resource_access[clientId].roles
 */
function extractRoles(profile: Record<string, unknown>, clientId: string): UserRole[] {
  const resourceAccess = profile.resource_access;
  
  // Validate resource_access is a non-null object
  if (typeof resourceAccess !== 'object' || resourceAccess === null) {
    return [];
  }

  const clientAccess = (resourceAccess as Record<string, unknown>)[clientId];
  
  // Validate clientAccess is a non-null object with roles array
  if (typeof clientAccess !== 'object' || clientAccess === null) {
    return [];
  }

  const roles = (clientAccess as Record<string, unknown>).roles;
  if (!Array.isArray(roles)) {
    return [];
  }

  // Filter to only valid roles
  const validRoles: UserRole[] = ['km-viewer', 'km-editor', 'km-admin'];
  return roles.filter((role): role is UserRole => 
    typeof role === 'string' && validRoles.includes(role as UserRole)
  );
}

/**
 * Extract user info from OIDC profile or decoded JWT claims
 */
function extractUser(profile: Record<string, unknown>, clientId: string): User {
  return {
    sub: (profile.sub as string) || '',
    email: (profile.email as string) || '',
    name: (profile.name as string) || (profile.preferred_username as string) || '',
    preferred_username: (profile.preferred_username as string) || '',
    roles: extractRoles(profile, clientId),
  };
}

/**
 * Decode the payload of a JWT without verifying the signature.
 * Used client-side to extract user claims from a Keycloak access token
 * during session restoration (the token was already validated by Keycloak
 * during the refresh exchange on the server side).
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // base64url → base64
    const base64 = parts[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const decoded = atob(base64);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Inner auth provider that uses the OIDC context
 */
function AuthProviderInner({ children, clientId }: { children: ReactNode; clientId: string }) {
  const oidc = useOidcAuth();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const refreshPromiseRef = useRef<Promise<string | null> | null>(null);

  // Session restoration state — used when OIDC has no session (e.g., browser
  // restart) but a valid offline refresh token still exists in the httpOnly cookie.
  const [isRestoringSession, setIsRestoringSession] = useState(true);
  const [restoredProfile, setRestoredProfile] = useState<Record<string, unknown> | null>(null);
  const sessionRestorationAttempted = useRef(false);

  // Extract user from OIDC profile, falling back to restored JWT claims
  const user = useMemo<User | null>(() => {
    if (oidc.user?.profile) {
      return extractUser(oidc.user.profile as Record<string, unknown>, clientId);
    }
    if (restoredProfile) {
      return extractUser(restoredProfile, clientId);
    }
    return null;
  }, [oidc.user?.profile, restoredProfile, clientId]);

  // Note: Refresh token storage is handled in the /callback page to avoid duplicate calls

  // Update access token when OIDC state changes
  useEffect(() => {
    if (oidc.user?.access_token) {
      setAccessToken(oidc.user.access_token);
    }
  }, [oidc.user?.access_token]);

  /**
   * Attempt to restore an authenticated session from the httpOnly cookie.
   *
   * When the browser restarts or sessionStorage is cleared, the OIDC library
   * loses its in-memory state. But the offline refresh token may still be valid
   * in the cookie. This effect tries a silent refresh before the app gives up
   * and redirects to the login page, avoiding a needless round-trip to Keycloak.
   */
  useEffect(() => {
    // Wait for OIDC to finish its own initialization
    if (oidc.isLoading) return;

    // If OIDC already has a session, no restoration needed
    if (oidc.isAuthenticated) {
      setIsRestoringSession(false);
      return;
    }

    // Only attempt restoration once
    if (sessionRestorationAttempted.current) return;
    sessionRestorationAttempted.current = true;

    const tryRestoreSession = async () => {
      try {
        // Check if a refresh token cookie exists
        const checkResponse = await fetch('/api/auth/token', {
          credentials: 'include',
        });
        const { hasRefreshToken } = await checkResponse.json();

        if (!hasRefreshToken) {
          setIsRestoringSession(false);
          return;
        }

        // Try to exchange the cookie refresh token for a new access token
        const refreshResponse = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        });

        if (refreshResponse.ok) {
          const data = await refreshResponse.json();
          setAccessToken(data.access_token);

          // Decode the Keycloak access token JWT to extract user claims
          // (the token was already validated server-side during the refresh)
          const claims = decodeJwtPayload(data.access_token);
          if (claims) {
            setRestoredProfile(claims);
          }
        }
      } catch (error) {
        console.error('Session restoration failed:', error);
      } finally {
        setIsRestoringSession(false);
      }
    };

    tryRestoreSession();
  }, [oidc.isLoading, oidc.isAuthenticated]);

  /**
   * Refresh the access token using the httpOnly cookie refresh token
   */
  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    // If a refresh is already in-flight, await it instead of starting another
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const doRefresh = async (): Promise<string | null> => {
      try {
        const response = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        });

        if (!response.ok) {
          setAccessToken(null);
          return null;
        }

        const data = await response.json();
        setAccessToken(data.access_token);
        return data.access_token;
      } catch (error) {
        console.error('Token refresh error:', error);
        setAccessToken(null);
        return null;
      } finally {
        refreshPromiseRef.current = null;
      }
    };

    refreshPromiseRef.current = doRefresh();
    return refreshPromiseRef.current;
  }, []);

  /**
   * Get a valid access token, refreshing if necessary
   */
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    // If we have a token from OIDC, check if it's still valid
    if (oidc.user?.access_token && oidc.user.expires_at) {
      const expiresAt = oidc.user.expires_at * 1000; // Convert to milliseconds
      const now = Date.now();
      const bufferMs = 60 * 1000; // 60 second buffer

      if (expiresAt - now > bufferMs) {
        // Token is still valid
        return oidc.user.access_token;
      }
    }

    // If we have a stored access token, use it (might need refresh)
    if (accessToken) {
      // Try to refresh
      return refreshAccessToken();
    }

    // No token available, try refresh in case there's a cookie
    return refreshAccessToken();
  }, [oidc.user?.access_token, oidc.user?.expires_at, accessToken, refreshAccessToken]);

  /**
   * Initialize the API client with token provider
   */
  useEffect(() => {
    setTokenProvider(getAccessToken, refreshAccessToken);
  }, [getAccessToken, refreshAccessToken]);

  /**
   * Login - redirect to Keycloak
   */
  const login = useCallback(() => {
    oidc.signinRedirect();
  }, [oidc]);

  /**
   * Logout - clear tokens and redirect to Keycloak logout
   */
  const logout = useCallback(async () => {
    try {
      // Clear server-side cookie and get logout URL
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });

      const data = await response.json();

      // Clear local state
      setAccessToken(null);
      setRestoredProfile(null);

      // Remove user from OIDC context
      await oidc.removeUser();

      // Redirect to Keycloak logout
      if (data.logoutUrl) {
        window.location.href = data.logoutUrl;
      } else {
        // Fallback: just redirect to login
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Fallback: redirect to login
      window.location.href = '/login';
    }
  }, [oidc]);

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback((role: UserRole): boolean => {
    return user?.roles.includes(role) ?? false;
  }, [user]);

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = useCallback((roles: UserRole[]): boolean => {
    return roles.some(role => user?.roles.includes(role));
  }, [user]);

  // Combine OIDC state with cookie-based session restoration
  const isAuthenticated = oidc.isAuthenticated || !!restoredProfile;
  const isLoading = oidc.isLoading || isRestoringSession;

  const value = useMemo<AuthContextType>(() => ({
    user,
    isAuthenticated,
    isLoading,
    error: oidc.error ?? null,
    login,
    logout,
    hasRole,
    hasAnyRole,
    getAccessToken,
  }), [
    user,
    isAuthenticated,
    isLoading,
    oidc.error,
    login,
    logout,
    hasRole,
    hasAnyRole,
    getAccessToken,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export interface AuthProviderProps {
  children: ReactNode;
  config: RuntimeAuthConfig;
}

/**
 * Auth provider component that wraps the app with OIDC authentication.
 * Receives runtime config as props from a Server Component.
 */
export function AuthProvider({ children, config }: AuthProviderProps) {
  const oidcConfig = buildOidcConfig(config);
  
  // Set API configuration synchronously before children render
  // This ensures the kernel API URL is available before any API calls
  useMemo(() => {
    setApiConfig({ kernelApiUrl: config.kernelApiUrl });
  }, [config.kernelApiUrl]);
  
  return (
    <OidcAuthProvider {...oidcConfig}>
      <AuthProviderInner clientId={config.keycloakClientId}>
        {children}
      </AuthProviderInner>
    </OidcAuthProvider>
  );
}

/**
 * Hook to access authentication state and functions
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
